import { makeAutoObservable, runInAction, computed } from 'mobx';
import { Service } from '../model/professional/service-model';
import { OptionCategory } from '../model/professional/option-category-model';
import { Option } from '../model/professional/option-model';
import userStore from './session-user-store';

/**
 * Type étendu pour les services avec catégories et options
 */
export interface CategoryWithOptions extends OptionCategory {
  options: Option[];
  // Ajout de la propriété isMultiple pour correspondre à isMultipleChoice dans le modèle
  isMultiple?: boolean;
}

export interface ServiceWithDetails extends Service {
  optionCategories: CategoryWithOptions[];
}

class ServiceStore {
  // État des services
  services: ServiceWithDetails[] = [];
  isLoading = false;
  error: string | null = null;
  professionalId: string | null = null;

  constructor() {
    makeAutoObservable(this);
    
    // Récupérer l'ID du professionnel depuis le store utilisateur
    this.professionalId = userStore.authUser?.user?.id || null;
  }

  /**
   * Méthode pour définir les services
   */
  setServices(services: ServiceWithDetails[]) {
    runInAction(() => {
      this.services = services;
    });
  }

  /**
   * Méthode pour obtenir un service par son ID
   */
  getServiceById(serviceId: string): ServiceWithDetails | undefined {
    return this.services.find(service => service.id === serviceId);
  }
  
  /**
   * Méthode pour ajouter un service
   */
  addService(service: ServiceWithDetails) {
    runInAction(() => {
      this.services = [...this.services, service];
    });
  }
  
  /**
   * Méthode pour mettre à jour un service
   */
  updateServiceInStore(updatedService: ServiceWithDetails) {
    runInAction(() => {
      const index = this.services.findIndex(service => service.id === updatedService.id);
      if (index !== -1) {
        this.services = [
          ...this.services.slice(0, index),
          updatedService,
          ...this.services.slice(index + 1)
        ];
      }
    });
  }
  
  /**
   * Méthode pour mettre à jour le statut d'un service
   */
  updateServiceStatus(serviceId: string, isActive: boolean) {
    runInAction(() => {
      const index = this.services.findIndex(service => service.id === serviceId);
      if (index !== -1) {
        const updatedService = { ...this.services[index], isActive };
        this.services = [
          ...this.services.slice(0, index),
          updatedService,
          ...this.services.slice(index + 1)
        ];
      }
    });
  }
  
  /**
   * Méthode pour supprimer un service
   */
  removeService(serviceId: string) {
    runInAction(() => {
      this.services = this.services.filter(service => service.id !== serviceId);
    });
  }


  /**
   * Getter pour tous les services
   */
  @computed
  get allServices() {
    return this.services;
  }
  
  /**
   * Getter pour les services actifs
   */
  @computed
  get activeServices() {
    return this.services.filter(service => service.isActive);
  }
  
  /**
   * Getter pour les services inactifs
   */
  @computed
  get inactiveServices() {
    return this.services.filter(service => !service.isActive);
  }
}

const serviceStore = new ServiceStore();
export default serviceStore;
